import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DataGrid, Panel, PanelGrid, SegmentedControl } from '@eo/shared';

import type { CapacityClientRow } from './api/types';
import { makeClientCapacityGridConfig } from './config/gridConfig';
import {
  CLIENT_GRID_PREFIX,
  CLIENT_ORDER_LABELS,
  CLIENT_ORDER_SORT,
  CLIENT_ORDERS,
  TEAM_LOAD_PARAM,
} from './constants';
import {
  clientFilterSource,
  personFilterSource,
  clientOrderOf,
  parseSelection,
  parseTeamLoadScope,
  portfolioName,
  selectionSearch,
} from './helpers';
import { MemberCapacity } from './MemberCapacity';
import { OverageAlert } from './OverageAlert';
import { OwnerRollupCards } from './OwnerRollupCards';
import { PortfolioSummary } from './PortfolioSummary';
import { SelectionDialog } from './SelectionDialog';
import { TeamLoad } from './TeamLoad';
import type { CapacityBoardProps, ClientOrder, TeamLoadScope } from './types';

export function CapacityBoard({
  data,
  isAdmin,
  meId,
  onPlan,
  onQueryOptions,
  onGenerateExcess,
}: CapacityBoardProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { report } = data;
  const name = portfolioName(report);
  const owner = report.users.find((u) => u.userId === report.scope.ownerUserId) ?? null;
  const selection = parseSelection(Object.fromEntries(searchParams.entries()));

  // The config is rebuilt only when the client list changes, so the link it
  // builds has to read the current URL through a ref.
  const paramsRef = useRef(searchParams);
  paramsRef.current = searchParams;
  const clientHref = useCallback(
    (clientId: string) => selectionSearch(paramsRef.current, { kind: 'client', id: clientId }),
    [],
  );
  const onTeamLoadScope = (scope: TeamLoadScope) =>
    setSearchParams(
      (params) => {
        if (scope === 'portfolio') params.delete(TEAM_LOAD_PARAM);
        else params.set(TEAM_LOAD_PARAM, scope);
        return params;
      },
      { replace: true },
    );
  const sortParam = `${CLIENT_GRID_PREFIX}sort`;
  const onClientOrder = (order: ClientOrder) =>
    setSearchParams(
      (params) => {
        if (order === 'attention') params.delete(sortParam);
        else params.set(sortParam, CLIENT_ORDER_SORT[order]);
        params.delete(`${CLIENT_GRID_PREFIX}page`);
        return params;
      },
      { replace: true },
    );
  const personHref = (userId: string) =>
    selectionSearch(searchParams, { kind: 'member', id: userId });
  const pickedClient = searchParams.get(`${CLIENT_GRID_PREFIX}client`);
  const clientsConfig = useMemo(
    () =>
      makeClientCapacityGridConfig({
        clientHref,
        clients: clientFilterSource(data, report, pickedClient, onQueryOptions),
        people: personFilterSource(data, report, null, onQueryOptions),
        onPlan,
        showOwner: report.scope.ownerUserId === null,
      }),
    [clientHref, data, report, pickedClient, onQueryOptions, onPlan],
  );

  return (
    <>
      <OverageAlert clients={report.variableOverage} clientHref={clientHref} />
      <PanelGrid>
        <Panel
          span={owner || isAdmin ? 1 : 2}
          title={name ? `${name}’s portfolio` : 'All portfolios'}
          subtitle="Engagement is every client’s effective budget this month, supplements included."
        >
          <PortfolioSummary report={report} />
        </Panel>
        {owner ? (
          <Panel
            title={owner.userId === meId ? 'My capacity' : `${owner.name}’s capacity`}
            subtitle="Committed client hours across every portfolio, against the hours available for client work."
          >
            <MemberCapacity
              member={owner}
              progress={report.progress}
              isMe={owner.userId === meId}
            />
          </Panel>
        ) : (
          isAdmin && (
            <Panel
              title="Portfolio by CFO"
              subtitle="Each CFO's book of business this month — budget, logged, and overage across every client they own."
            >
              <OwnerRollupCards rows={report.portfolios} />
            </Panel>
          )
        )}
        <Panel
          span={2}
          title="Team load"
          subtitle="Hours logged against hours assigned, on this portfolio or across every one. Pick a person to see their detail."
        >
          <TeamLoad
            users={report.users}
            across={report.acrossPortfolios}
            progress={report.progress}
            personHref={personHref}
            // On the all portfolios view both loads are the same one.
            scope={
              report.scope.ownerUserId === null
                ? null
                : parseTeamLoadScope(searchParams.get(TEAM_LOAD_PARAM))
            }
            onScopeChange={onTeamLoadScope}
          />
        </Panel>
        {/* Spans the canvas: a budget-versus-actual grid is unreadable in half a
          column. */}
        <Panel
          span={2}
          title="Clients"
          subtitle="Each client’s fixed and variable hours against their budgets, who holds them, and how far over. The pencil plans a client; click a card to see its detail."
        >
          <DataGrid<CapacityClientRow>
            config={clientsConfig}
            rows={data.clientRows}
            pagination={data.clientsPagination}
            headerActions={
              <SegmentedControl<ClientOrder>
                options={CLIENT_ORDERS.map((value) => ({
                  value,
                  label: CLIENT_ORDER_LABELS[value],
                }))}
                value={clientOrderOf(searchParams.get(sortParam))}
                onChange={onClientOrder}
                ariaLabel="Client order"
                className="w-fit"
              />
            }
          />
        </Panel>
      </PanelGrid>
      {selection && (
        <SelectionDialog
          data={data}
          selection={selection}
          meId={meId}
          onQueryOptions={onQueryOptions}
          onGenerateExcess={onGenerateExcess}
        />
      )}
    </>
  );
}
